import { useSyncExternalStore } from 'react';
import { Breakpoints } from '@picsart/design-system/foundation/responsive';

interface DeviceViewport {
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
}

const tabletQuery = `(min-width: ${Breakpoints.md}px)`;
const desktopQuery = `(min-width: ${Breakpoints.lg}px)`;

let tabletMq: MediaQueryList | null = null;
let desktopMq: MediaQueryList | null = null;

const getTabletMq = () => {
  if (!tabletMq) tabletMq = window.matchMedia(tabletQuery);
  return tabletMq;
};

const getDesktopMq = () => {
  if (!desktopMq) desktopMq = window.matchMedia(desktopQuery);
  return desktopMq;
};

const buildSnapshot = (): DeviceViewport => {
  const isDesktop = getDesktopMq().matches;
  const isTabletOrAbove = getTabletMq().matches;
  return {
    isMobile: !isTabletOrAbove,
    isTablet: isTabletOrAbove && !isDesktop,
    isDesktop,
  };
};

// Module-level cache of the most recent snapshot.
//
// Why a cache: `useSyncExternalStore`'s `getSnapshot` must return a STABLE
// reference between renders when the underlying value hasn't changed —
// otherwise React tears down and re-runs subscribers every render. Returning
// `buildSnapshot()` from `getSnapshot` directly would create a new object on
// every read and cause an infinite render loop.
//
// `cached` is refreshed in two places:
//   1. At module load (initial value)
//   2. Inside `onChange` whenever a media query crosses a threshold
//
// We also refresh `cached` at the top of `subscribe` to defend against the
// stale-cache window: if the viewport changes between module load and the
// first subscriber's mount, no `onChange` has fired yet and `cached` could
// be wrong. Reading it once on subscribe closes that gap.
let cached: DeviceViewport = buildSnapshot();

const refreshCachedIfChanged = () => {
  const next = buildSnapshot();
  if (
    next.isMobile !== cached.isMobile ||
    next.isTablet !== cached.isTablet ||
    next.isDesktop !== cached.isDesktop
  ) {
    cached = next;
    return true;
  }
  return false;
};

const subscribe = (cb: () => void) => {
  const tMq = getTabletMq();
  const dMq = getDesktopMq();

  // Close the stale-cache window: if the viewport changed between module
  // load and this subscriber mounting, notify React on the next tick.
  if (refreshCachedIfChanged()) queueMicrotask(cb);

  // Always notify this subscriber on a MQ change. The cache is shared, so
  // if we only called cb when WE flipped it, the first subscriber to fire
  // would win and all others would never re-render. React de-dupes via
  // Object.is on the snapshot reference, so over-notifying is cheap.
  const onChange = () => {
    refreshCachedIfChanged();
    cb();
  };

  tMq.addEventListener('change', onChange);
  dMq.addEventListener('change', onChange);
  return () => {
    tMq.removeEventListener('change', onChange);
    dMq.removeEventListener('change', onChange);
  };
};

const getSnapshot = () => cached;

/**
 * Reactive viewport hook — returns device type based on viewport width.
 * Uses `matchMedia` for native threshold detection, no resize polling.
 *
 * Reactivity model:
 *   - Backed by `useSyncExternalStore` against module-level matchMedia
 *     listeners. React subscribes to viewport changes once per consumer,
 *     re-renders only when isMobile/isTablet/isDesktop flip.
 *   - The snapshot object is cached and only re-allocated on a real
 *     threshold cross, so equality-based bailouts (memo, etc.) work.
 *
 * Thresholds:
 *   - isMobile:  < 1024px (Breakpoints.md)
 *   - isTablet:  >= 1024px and < 1280px (Breakpoints.lg)
 *   - isDesktop: >= 1280px
 */
export const useDeviceViewport = () =>
  useSyncExternalStore(subscribe, getSnapshot);
